import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CheckinLogDTO, EventAttendeeDTO, EventDetailDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { PromptModal, cuando, errorText } from "./comun";
import { beep, unlockAudio, useBarcodeCamera } from "../../../../components/escaner/camara";

interface Props { campusId: number; evento: EventDetailDTO; soloLectura: boolean; reload: () => void; notify: (t: string, ok?: boolean) => void; }

interface Lectura { ok: boolean; message: string; attendee: EventAttendeeDTO | null; status: number; }

/** El mismo QR a la vista no vuelve a leerse hasta que pasa este tiempo (la puerta tarda en quitar la invitación). */
const MISMO_CODIGO_MS = 4000;

/** Cámara del celular leyendo los QR de las invitaciones (lector compartido con Bodegas). */
const Camara: React.FC<{ onCodigo: (qr: string) => void; ultima: Lectura | null; onClose: () => void }> = ({ onCodigo, ultima, onClose }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const visto = useRef<Map<string, number>>(new Map());
	const [flash, setFlash] = useState<boolean>(false);
	const estado = useBarcodeCamera(videoRef, true, (codes) => {
		const ahora = Date.now();
		for (const c of codes) {
			const antes = visto.current.get(c) || 0;
			visto.current.set(c, ahora);
			if (ahora - antes < MISMO_CODIGO_MS) continue;
			beep(); setFlash(true); setTimeout(() => setFlash(false), 500);
			onCodigo(c);
			break;
		}
	});
	useEffect(() => { unlockAudio(); }, []);
	useEffect(() => {
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
		window.addEventListener("keydown", esc);
		return () => window.removeEventListener("keydown", esc);
	}, [onClose]);
	const pie = estado === "starting" ? S.camaraPrendiendo : estado === "denied" ? S.camaraSinPermiso : estado === "unsupported" ? S.camaraNoSoporta : S.camaraApunta;
	return (
		<div className="wev-cam" role="dialog" aria-modal="true" aria-label={S.camaraTitulo} onPointerDown={unlockAudio}>
			<div className="wev-cam-barra">
				<h3>{S.camaraTitulo}</h3>
				<button type="button" className="wau-btn s" onClick={onClose}>{S.cerrarCamara}</button>
			</div>
			<div className="wev-cam-video">
				<video ref={videoRef} muted playsInline />
				<div className={"wev-cam-marco" + (flash ? " ok" : "")} />
			</div>
			<div className="wev-cam-pie">
				{ultima ? <span className={ultima.ok ? "" : "wau-oro"} style={{ fontWeight: 600 }}>{ultima.message}</span> : pie}
				{ultima && ultima.attendee ? <span className="sub">{[ultima.attendee.name, ultima.attendee.kindLabel, ultima.attendee.tableNo ? S.mesa + " " + ultima.attendee.tableNo : null].filter(Boolean).join(" · ")}</span> : ultima ? <span className="sub">{pie}</span> : null}
			</div>
		</div>
	);
};

/** Control de entrada: el lector de QR escribe el código y da Enter; o la cámara del celular. */
const EntradaTab: React.FC<Props> = ({ campusId, evento, soloLectura, reload, notify }) => {
	const http = HttpManager.getInstance();
	const [codigo, setCodigo] = useState<string>("");
	const [lectura, setLectura] = useState<Lectura | null>(null);
	const [log, setLog] = useState<CheckinLogDTO[] | null>(null);
	const [rehab, setRehab] = useState<EventAttendeeDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [camara, setCamara] = useState<boolean>(false);
	const campo = useRef<HTMLInputElement>(null);
	const busyRef = useRef<boolean>(false);
	const vigentes = evento.attendees.filter(a => a.status === 1);
	const adentro = vigentes.filter(a => a.checkedInAt).length;
	const conCamara = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

	useEffect(() => { campo.current?.focus(); }, []);

	const verLog = (a: EventAttendeeDTO | null) => {
		setLog(null);
		if (a) http.eventsService.checkins(campusId, a.id).then(r => setLog(Array.isArray(r.data) ? r.data : [])).catch(() => setLog([]));
	};
	const leer = (texto?: string) => {
		const qr = (texto ?? codigo).trim();
		if (!qr || busyRef.current) return;
		busyRef.current = true; setBusy(true);
		const local = evento.attendees.find(a => a.qrCode === qr) || null;
		/*
			Con un 409/422 el servidor no devuelve la asistente: se vuelve a pedir la lista del evento (la cargada puede no
			traerla o estar vieja) antes de ofrecer Re-habilitar; si esa consulta falla, queda la de la lista cargada.
		*/
		const buscarFresca = (): Promise<EventAttendeeDTO | null> =>
			http.eventsService.details(campusId, evento.id)
				.then(r => (r.data?.attendees || []).find(a => a.qrCode === qr) || local)
				.catch(() => local);
		http.eventsService.checkin(campusId, qr)
			.then(r => { setLectura({ ok: true, message: r.data.message, attendee: r.data.attendee || local, status: 200 }); verLog(r.data.attendee || local); reload(); })
			.catch(async e => {
				const status: number = e?.response?.status || 0;
				const enRespuesta: EventAttendeeDTO | null = e?.response?.data?.attendee || null;
				const asistente = enRespuesta || ((status === 409 || status === 422) ? await buscarFresca() : local);
				if (!enRespuesta && (status === 409 || status === 422)) reload();
				setLectura({ ok: false, message: errorText(e), attendee: asistente, status });
				verLog(asistente);
			})
			.finally(() => { busyRef.current = false; setBusy(false); setCodigo(""); if (!camara) campo.current?.focus(); });
	};

	return (
		<div style={{ paddingBottom: 12 }}>
			<div style={{ marginBottom: 8 }}><span className="wevChip filo wau-num">{S.entradas(adentro, vigentes.length)}</span></div>
			<div className="wau-fila">
				<label className="wau-campo" style={{ flex: "1 1 320px", maxWidth: 540 }}><span className="wau-lab">{S.codigo}</span>
					<input ref={campo} className="wau-entrada wev-entrada-qr" value={codigo} disabled={soloLectura} autoComplete="off"
						onChange={e => setCodigo(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); leer(); } }} /></label>
				<button type="button" className="wau-btn p" disabled={soloLectura || busy || !codigo.trim()} onClick={() => leer()}>{S.leer}</button>
				{conCamara ? <button type="button" className="wau-btn s" disabled={soloLectura} onClick={() => { setLectura(null); setCamara(true); }}>{S.escanearCamara}</button> : null}
			</div>
			{lectura ? (
				<div className={"wev-lectura " + (lectura.ok ? "ok" : "mal")} role="status">
					{lectura.message}
					{lectura.attendee ? <span className="sub">{[lectura.attendee.name, lectura.attendee.kindLabel, lectura.attendee.tableNo ? S.mesa + " " + lectura.attendee.tableNo : null, lectura.attendee.hostName ? S.invitaA(lectura.attendee.hostName) : null].filter(Boolean).join(" · ")}</span> : null}
					{!lectura.ok && lectura.status === 409 && lectura.attendee && !soloLectura ? (
						<div style={{ marginTop: 10 }}><button type="button" className="wau-btn s" onClick={() => setRehab(lectura.attendee)}>{S.rehabilitar}</button></div>
					) : null}
				</div>
			) : null}
			{log && log.length > 0 ? (
				<div className="wau-hilo">
					<span className="wau-lab">{S.lecturas}</span>
					{log.map(l => <div key={l.id}><span className="cuando wau-num">{cuando(l.at)}</span>{S.acciones[l.action] || l.action}{l.by ? " · " + l.by : ""}{l.note ? " · " + l.note : ""}</div>)}
				</div>
			) : null}
			{camara ? <Camara onCodigo={(qr) => leer(qr)} ultima={lectura} onClose={() => { setCamara(false); campo.current?.focus(); }} /> : null}
			{rehab ? <PromptModal title={S.rehabTitulo(rehab.name)} label={S.rehabNota} confirm={S.rehabilitar} busy={busy} onCancel={() => setRehab(null)}
				onConfirm={(nota) => {
					setBusy(true);
					http.eventsService.reenable(campusId, rehab.id, nota)
						.then(() => { notify(S.rehabOk); setLectura(null); verLog(rehab); setRehab(null); reload(); })
						.catch(e => notify(errorText(e), false))
						.finally(() => { setBusy(false); campo.current?.focus(); });
				}} /> : null}
		</div>
	);
};

export default EntradaTab;
